<template>
  <div>
    <b-sidebar
      id="sidebar-store-campaign"
      sidebar-class="sidebar-lg"
      :visible="newCampaignSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => mutateNewCampaignSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4>Nova campanha</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <b-form-group
            label="Apelido"
            label-for="new-campaign-alias"
          >
            <b-form-input
              id="new-campaign-alias"
              v-model="alias"
              :class="{ 'is-invalid': v$.alias.$error }"
            />
            <div class="invalid-feedback">
              <span v-if="v$.alias.required.$invalid">
                Você deve informar um apelido
              </span>
              <span v-if="v$.alias.regex.$invalid">
                Utilize apenas letras, números e espaços
              </span>
              <span v-if="v$.alias.notPn.$invalid">
                O apelido não pode ser "pn"
              </span>
            </div>
          </b-form-group>

          <b-form-group v-if="isCustomerCampaign">
            <b-form-checkbox
              id="new-campaign-has-business-unit"
              v-model="hasBusinessUnit"
              @change="onBusinessUnitToggle"
            >
              Quero direcionar o lead para uma Unidade de negócio
            </b-form-checkbox>
          </b-form-group>

          <b-form-group
            v-if="hasBusinessUnit"
            label="Unidade de negócio"
            label-for="new-campaign-business-unit"
          >
            <v-select
              id="new-campaign-business-unit"
              v-model="businessUnitId"
              :options="businessUnitOptions"
              :loading="loadingBusinessUnits"
              :reduce="(businessUnit) => businessUnit.id"
              label="name"
              :class="getSelectErrorClass(v$.businessUnitId.$error)"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
            <div class="invalid-feedback">
              <span v-if="v$.businessUnitId.required.$invalid">
                Você deve selecionar uma unidade de negócio
              </span>
            </div>
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              :disabled="saving"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormFile, BAvatar, BButton, BFormInvalidFeedback, BFormTextarea, BFormCheckbox
} from 'bootstrap-vue'
import { mapMutations, mapGetters, mapActions } from 'vuex'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import * as types from '../store/types'
import * as sharedTypes from '@/modules/shared/store/types'
import { getVueSelectErrorClass } from '@/helpers/validators'
import { LEAD_TYPE_PCL, CAMPAIGN_FINANCIAL_PLANNING_OPTION } from '@/constants/lead'

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    vSelect
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      saving: false,
      alias: undefined,
      hasBusinessUnit: false,
      businessUnitId: undefined,
      loadingBusinessUnits: false
    }
  },
  computed: {
    ...mapGetters({
      newCampaignSidebar: types.NEW_CAMPAIGN_SIDEBAR,
      businessUnits: sharedTypes.BUSINESS_UNITS
    }),
    // Futuro Planejamento Financeiro: o lead fica com o dono do link e vai para a pré-entrevista por IA
    businessUnitOptions() {
      return [
        { id: CAMPAIGN_FINANCIAL_PLANNING_OPTION, name: 'Futuro Planejamento Financeiro' },
        ...(this.businessUnits || []),
      ]
    },
    leadType() {
      return this.newCampaignSidebar.leadType || LEAD_TYPE_PCL
    },
    isCustomerCampaign() {
      return this.leadType === LEAD_TYPE_PCL
    }
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations(){
    return {
      alias: {
        required,
        regex(value) {
          if (value) {
            const pattern = /^[a-zA-Z0-9 ]+$/;
            return pattern.test(value);
          }
          return true;
        },
        notPn(value) {
          return !value || value.trim().toLowerCase() !== 'pn';
        },
      },
      businessUnitId: {
        required: requiredIf(this.hasBusinessUnit),
      },
    }
  },
  methods: {
    ...mapMutations({
      mutateNewCampaignSidebar: types.MUTATE_NEW_CAMPAIGN_SIDEBAR
    }),
    ...mapActions({
      storeCampaign: types.STORE_CAMPAIGN,
      getBusinessUnits: sharedTypes.GET_BUSINESS_UNITS
    }),
    onBusinessUnitToggle(checked) {
      this.businessUnitId = undefined
      if (!checked) return
      this.loadingBusinessUnits = true
      this.getBusinessUnits()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar as unidades de negócio. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loadingBusinessUnits = false
        })
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError)
    },
    clear(){
      this.alias = undefined
      this.hasBusinessUnit = false
      this.businessUnitId = undefined
      this.v$.$reset();
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return
      this.saving = true
      let newAlias = this.alias.replace(/\s+/g, "-"); // Substitui espaços por hífens.
      newAlias = newAlias.normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // Remove acentos.
      const isFinancialPlanning = this.hasBusinessUnit && this.businessUnitId === CAMPAIGN_FINANCIAL_PLANNING_OPTION
      this.storeCampaign({
        alias: newAlias,
        lead_type: this.leadType,
        business_unit_id: this.hasBusinessUnit && !isFinancialPlanning ? this.businessUnitId : null,
        ai_pre_interview: isFinancialPlanning
      })
      .then(response => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "A campanha foi criada com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.mutateNewCampaignSidebar({visible: false})
          this.newCampaignSidebar.saveAction()
        })
      .catch((err) => {
        if(err.response.status == 405){
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: err.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }else{
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao criar a campanha. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      })
      .finally(() => {
        this.saving = false;
      });
    }
  }
}
</script>
